import { useTranslation } from 'react-i18next';
import { DollarSign, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { useCurrency } from '../context/CurrencyContext';

export default function CurrencySwitcher({ variant = 'icon' }) {
  const { t } = useTranslation();
  const { currency, setCurrency, currenciesMeta } = useCurrency();

  const meta = currenciesMeta[currency] || { symbol: '€', name: 'Euro' };
  const codes = Object.keys(currenciesMeta).length ? Object.keys(currenciesMeta) : ['EUR', 'USD', 'GBP', 'JPY'];

  const trigger = variant === 'full' ? (
    <button
      data-testid="currency-switcher-full"
      className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg border border-amber-100 hover:border-amber-300 hover:bg-amber-50 transition-all"
    >
      <DollarSign size={14} className="text-amber-500" />
      <span className="font-medium text-obsidian">{currency} · {meta.name}</span>
    </button>
  ) : (
    <button
      data-testid="currency-switcher"
      aria-label={t('common.currency') !== 'common.currency' ? t('common.currency') : 'Currency'}
      className="p-1.5 rounded-lg hover:bg-amber-50 transition-colors flex items-center gap-1"
    >
      <span className="text-[11px] font-semibold text-obsidian/70 tracking-wider">{currency}</span>
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto rounded-lg shadow-xl border-amber-100/50" data-testid="currency-switcher-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-amber-600/70 font-semibold">
          {t('common.currency') !== 'common.currency' ? t('common.currency') : 'Currency'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {codes.map(code => {
          const info = currenciesMeta[code] || {};
          const isActive = code === currency;
          return (
            <DropdownMenuItem
              key={code}
              onClick={() => setCurrency(code)}
              data-testid={`currency-option-${code}`}
              className={`text-xs flex items-center justify-between gap-2 ${isActive ? 'bg-amber-50/70' : ''}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-[10px] tracking-wider font-mono text-amber-600 w-8">{code}</span>
                <span className="truncate">{info.name || code}</span>
              </span>
              {isActive && <Check size={12} className="text-amber-500" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
